import { useEffect, useReducer, useRef, useState } from "react";
import { Box, Button, Chip, CircularProgress, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";

import useLanguage from "../../hooks/useLanguage";
import useToaster from "../../hooks/useToaster";
import useOverlayTemplates from "../../hooks/useOverlayTemplates";
import LayersPanel from "./components/LayersPanel";
import PropertiesPanel from "./components/PropertiesPanel";
import CanvasStage from "./components/CanvasStage";
import CanvasToolbar from "./components/CanvasToolbar";
import { EditorBody, EditorRoot, EditorTopBar, LeftPanel, RightPanel, CenterPanel } from "./style";
import { PRESETS } from "./presets";

const CANVAS = { w: 1080, h: 1920 };

function makeId() {
  return `el_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

function defaultElement(type, existingElements) {
  const maxZ = existingElements.reduce((m, e) => Math.max(m, e.z || 0), 0);
  const z = maxZ + 1;
  switch (type) {
    case "text":
      return {
        id: makeId(), type: "text", x: 90, y: 1720, w: 900, h: 120, z,
        rotation: 0, opacity: 1, label: `Text ${z}`, defaultText: "Sample text",
        font: "Heebo", size: 80, weight: 700, color: "#ffffff", align: "center",
      };
    case "logo":
      return { id: makeId(), type: "logo", x: 780, y: 80, w: 220, h: 220, z, rotation: 0, opacity: 1 };
    case "gradient":
      return {
        id: makeId(), type: "gradient", x: 0, y: 1248, w: 1080, h: 672, z,
        rotation: 0, opacity: 1, direction: 270,
        stops: [
          { color: "#000000", position: 0, alpha: 0 },
          { color: "#000000", position: 1, alpha: 0.8 },
        ],
      };
    case "shape":
      return {
        id: makeId(), type: "shape", x: 90, y: 1700, w: 900, h: 200, z,
        rotation: 0, opacity: 1, kind: "rect", fill: "#000000", alpha: 0.55, radius: 16,
      };
    default:
      return { id: makeId(), type, x: 100, y: 100, w: 400, h: 200, z, rotation: 0, opacity: 1 };
  }
}

const HISTORY_LIMIT = 50;
const MUTATING_ACTIONS = new Set([
  "ADD", "UPDATE", "REMOVE", "REORDER", "TOGGLE_VISIBLE",
  "BRING_FORWARD", "SEND_BACKWARD", "BRING_FRONT", "SEND_BACK",
  "DUPLICATE", "NUDGE",
]);

function snapshot(s) {
  return { elements: s.elements, selectedId: s.selectedId, isDirty: s.isDirty };
}

function withHistory(reducerFn) {
  return (state, action) => {
    if (action.type === "UNDO") {
      if (!state.past.length) return state;
      const prev = state.past[state.past.length - 1];
      return {
        ...state,
        ...prev,
        past: state.past.slice(0, -1),
        future: [...state.future, snapshot(state)],
        isDirty: true,
      };
    }
    if (action.type === "REDO") {
      if (!state.future.length) return state;
      const next = state.future[state.future.length - 1];
      return {
        ...state,
        ...next,
        past: [...state.past, snapshot(state)],
        future: state.future.slice(0, -1),
        isDirty: true,
      };
    }
    const nextState = reducerFn(state, action);
    if (nextState === state) return state;
    if (MUTATING_ACTIONS.has(action.type)) {
      // Coalesce consecutive UPDATE bursts to the same element/keys within 600ms
      // so dragging or typing produces ONE undo step, not dozens.
      const now = Date.now();
      const lastEntry = state.past[state.past.length - 1];
      const tag = action.type === "UPDATE"
        ? `${action.id}|${Object.keys(action.patch || {}).sort().join(",")}`
        : null;
      const coalesce = tag &&
        state._lastUpdateTag === tag &&
        now - (state._lastUpdateAt || 0) < 600 &&
        lastEntry;
      const past = coalesce
        ? state.past
        : [...state.past, snapshot(state)].slice(-HISTORY_LIMIT);
      return { ...nextState, past, future: [], _lastUpdateTag: tag, _lastUpdateAt: now };
    }
    return nextState;
  };
}

function baseReducer(state, action) {
  switch (action.type) {
    case "SET_ALL":
      return {
        ...state,
        elements: action.elements,
        selectedId: action.selectedId ?? null,
        isDirty: false,
        past: [],
        future: [],
      };

    case "ADD": {
      const el = defaultElement(action.elementType, state.elements);
      return { ...state, elements: [...state.elements, el], selectedId: el.id, isDirty: true };
    }

    case "UPDATE": {
      const elements = state.elements.map((el) =>
        el.id === action.id ? { ...el, ...action.patch } : el
      );
      return { ...state, elements, isDirty: true };
    }

    case "REMOVE": {
      const elements = state.elements.filter((el) => el.id !== action.id);
      return {
        ...state,
        elements,
        selectedId: state.selectedId === action.id ? null : state.selectedId,
        isDirty: true,
      };
    }

    case "SELECT":
      return { ...state, selectedId: action.id };

    case "TOGGLE_VISIBLE": {
      const elements = state.elements.map((el) =>
        el.id === action.id ? { ...el, _hidden: !el._hidden } : el
      );
      return { ...state, elements };
    }

    case "REORDER": {
      const fromEl = state.elements.find((e) => e.id === action.fromId);
      const toEl = state.elements.find((e) => e.id === action.toId);
      if (!fromEl || !toEl) return state;
      const fromZ = fromEl.z;
      const toZ = toEl.z;
      const elements = state.elements.map((el) => {
        if (el.id === action.fromId) return { ...el, z: toZ };
        if (el.id === action.toId) return { ...el, z: fromZ };
        return el;
      });
      return { ...state, elements, isDirty: true };
    }

    case "BRING_FORWARD": {
      const el = state.elements.find((e) => e.id === action.id);
      if (!el) return state;
      const above = state.elements
        .filter((e) => e.id !== action.id && e.z > el.z)
        .sort((a, b) => a.z - b.z)[0];
      if (!above) return state;
      const elZ = el.z;
      const aboveZ = above.z;
      return {
        ...state,
        elements: state.elements.map((e) => {
          if (e.id === action.id) return { ...e, z: aboveZ };
          if (e.id === above.id) return { ...e, z: elZ };
          return e;
        }),
        isDirty: true,
      };
    }

    case "SEND_BACKWARD": {
      const el = state.elements.find((e) => e.id === action.id);
      if (!el) return state;
      const below = state.elements
        .filter((e) => e.id !== action.id && e.z < el.z)
        .sort((a, b) => b.z - a.z)[0];
      if (!below) return state;
      const elZ = el.z;
      const belowZ = below.z;
      return {
        ...state,
        elements: state.elements.map((e) => {
          if (e.id === action.id) return { ...e, z: belowZ };
          if (e.id === below.id) return { ...e, z: elZ };
          return e;
        }),
        isDirty: true,
      };
    }

    case "BRING_FRONT": {
      const maxZ = state.elements.reduce((m, e) => Math.max(m, e.z || 0), 0);
      return {
        ...state,
        elements: state.elements.map((e) =>
          e.id === action.id ? { ...e, z: maxZ + 1 } : e
        ),
        isDirty: true,
      };
    }

    case "SEND_BACK": {
      const minZ = state.elements.reduce((m, e) => Math.min(m, e.z || 0), Infinity);
      return {
        ...state,
        elements: state.elements.map((e) =>
          e.id === action.id ? { ...e, z: minZ - 1 } : e
        ),
        isDirty: true,
      };
    }

    case "DUPLICATE": {
      const original = state.elements.find((e) => e.id === action.id);
      if (!original) return state;
      const maxZ = state.elements.reduce((m, e) => Math.max(m, e.z || 0), 0);
      const clone = { ...original, id: makeId(), x: original.x + 20, y: original.y + 20, z: maxZ + 1 };
      return { ...state, elements: [...state.elements, clone], selectedId: clone.id, isDirty: true };
    }

    case "NUDGE": {
      const el = state.elements.find((e) => e.id === action.id);
      if (!el) return state;
      return {
        ...state,
        elements: state.elements.map((e) =>
          e.id === action.id ? { ...e, x: e.x + (action.dx || 0), y: e.y + (action.dy || 0) } : e
        ),
        isDirty: true,
      };
    }

    case "MARK_CLEAN":
      return { ...state, isDirty: false };

    default:
      return state;
  }
}

const reducer = withHistory(baseReducer);

const initialState = { elements: [], selectedId: null, isDirty: false, past: [], future: [] };

export default function OverlayTemplateEditor() {
  const [{ pages }] = useLanguage();
  const t = pages.overlayTemplates || {};
  const { addToaster } = useToaster();
  const { get, create, update, uploadLogo } = useOverlayTemplates();

  const [state, dispatch] = useReducer(reducer, initialState);
  const [templateId, setTemplateId] = useState(null);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const containerRef = useRef(null);
  const canvasAreaRef = useRef(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const parts = window.location.pathname.split("/");
    const idSegment = parts[parts.indexOf("overlay-templates") + 1];
    if (idSegment && idSegment !== "new") {
      setTemplateId(idSegment);
      setLoading(true);
      get(idSegment)
        .then((data) => {
          setName(data.name || "");
          const elements = (data.elements || []).map((el) =>
            el.type === "logo" && el.url ? { ...el, _previewUrl: el.url } : el,
          );
          dispatch({ type: "SET_ALL", elements });
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    } else {
      // Check for preset passed via history state
      const preset = window.history.state?.preset;
      if (preset && PRESETS[preset]) {
        const elements = PRESETS[preset];
        const firstText = elements.find((e) => e.type === "text");
        dispatch({
          type: "SET_ALL",
          elements,
          selectedId: firstText ? firstText.id : (elements[0]?.id ?? null),
        });
      }
    }
  }, []); // intentionally empty — run once on mount

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e) => {
      const tag = document.activeElement?.tagName;
      const isEditing =
        tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" ||
        document.activeElement?.isContentEditable;

      if (e.key === "Escape") {
        dispatch({ type: "SELECT", id: null });
        return;
      }

      // Undo/Redo work whether or not user is in an input
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "z") {
        e.preventDefault();
        dispatch({ type: e.shiftKey ? "REDO" : "UNDO" });
        return;
      }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "y") {
        e.preventDefault();
        dispatch({ type: "REDO" });
        return;
      }

      if (isEditing) return;

      const selId = state.selectedId;

      if ((e.key === "Delete" || e.key === "Backspace") && selId) {
        dispatch({ type: "REMOVE", id: selId });
        return;
      }

      if ((e.metaKey || e.ctrlKey) && e.key === "d" && selId) {
        e.preventDefault();
        dispatch({ type: "DUPLICATE", id: selId });
        return;
      }

      if ((e.metaKey || e.ctrlKey) && e.key === "]" && selId) {
        e.preventDefault();
        dispatch({ type: "BRING_FORWARD", id: selId });
        return;
      }

      if ((e.metaKey || e.ctrlKey) && e.key === "[" && selId) {
        e.preventDefault();
        dispatch({ type: "SEND_BACKWARD", id: selId });
        return;
      }

      if (selId) {
        const step = e.shiftKey ? 10 : 1;
        if (e.key === "ArrowLeft") { e.preventDefault(); dispatch({ type: "NUDGE", id: selId, dx: -step, dy: 0 }); }
        if (e.key === "ArrowRight") { e.preventDefault(); dispatch({ type: "NUDGE", id: selId, dx: step, dy: 0 }); }
        if (e.key === "ArrowUp") { e.preventDefault(); dispatch({ type: "NUDGE", id: selId, dx: 0, dy: -step }); }
        if (e.key === "ArrowDown") { e.preventDefault(); dispatch({ type: "NUDGE", id: selId, dx: 0, dy: step }); }
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [state.selectedId]);

  const selectedElement = state.elements.find((el) => el.id === state.selectedId) || null;

  const handleBack = () => {
    window.history.pushState({}, "", "/overlay-templates");
    window.dispatchEvent(new PopStateEvent("popstate"));
  };

  const cleanElements = (elements) =>
    elements.map(({ _hidden, _previewUrl, url, ...el }) => el);

  const persist = async ({ silent = false } = {}) => {
    const effectiveName = name.trim() || (t.untitled || "Untitled Template");
    if (!name.trim()) setName(effectiveName);
    const payload = {
      name: effectiveName,
      canvas: CANVAS,
      elements: cleanElements(state.elements),
    };
    if (templateId) {
      await update(templateId, payload);
      if (!silent) addToaster({ message: t.saved || "Template saved", severity: "success" });
      dispatch({ type: "MARK_CLEAN" });
      return templateId;
    }
    const result = await create(payload);
    const newId = result.id;
    setTemplateId(newId);
    dispatch({ type: "MARK_CLEAN" });
    if (!silent) addToaster({ message: t.created || "Template created", severity: "success" });
    window.history.replaceState({}, "", `/overlay-templates/${newId}`);
    return newId;
  };

  const handleSave = async () => {
    if (state.elements.length === 0) return;
    setSaving(true);
    try {
      await persist();
    } catch {
      // error shown by hook
    } finally {
      setSaving(false);
    }
  };

  const ensureTemplate = async () => {
    if (templateId) return templateId;
    setSaving(true);
    try {
      return await persist({ silent: true });
    } finally {
      setSaving(false);
    }
  };

  const canSave = name.trim().length > 0 && state.elements.length > 0 && !saving;

  if (loading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <EditorRoot>
      <EditorTopBar>
        <Tooltip title={t.back || "Back"}>
          <IconButton size="small" onClick={handleBack}>
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>

        <TextField
          size="small"
          placeholder={t.namePlaceholder || "Template name (required)"}
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ width: 280 }}
          inputProps={{ maxLength: 100 }}
        />

        {state.isDirty && (
          <Chip label={t.unsaved || "Unsaved changes"} size="small" color="warning" variant="outlined" />
        )}

        <Box sx={{ flex: 1 }} />

        <Tooltip title={t.undo || "Undo (Ctrl+Z)"}>
          <span>
            <IconButton
              size="small"
              onClick={() => dispatch({ type: "UNDO" })}
              disabled={!state.past?.length}
            >
              <UndoIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={t.redo || "Redo (Ctrl+Shift+Z)"}>
          <span>
            <IconButton
              size="small"
              onClick={() => dispatch({ type: "REDO" })}
              disabled={!state.future?.length}
            >
              <RedoIcon />
            </IconButton>
          </span>
        </Tooltip>

        <Button
          variant="contained"
          color="action"
          disabled={!canSave}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
        >
          {saving ? (t.saving || "Saving…") : (t.save || "Save")}
        </Button>
      </EditorTopBar>

      <EditorBody>
        <LeftPanel>
          <LayersPanel
            elements={state.elements}
            selectedId={state.selectedId}
            dispatch={dispatch}
          />
        </LeftPanel>

        <CenterPanel ref={containerRef}>
          <Box sx={{ display: "flex", flexDirection: "column", width: "100%", height: "100%", overflow: "hidden" }}>
            <CanvasToolbar
              selectedElement={selectedElement}
              dispatch={dispatch}
            />
            <Box ref={canvasAreaRef} sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
              <CanvasStage
                elements={state.elements}
                selectedId={state.selectedId}
                dispatch={dispatch}
                containerRef={canvasAreaRef}
              />
            </Box>
          </Box>
        </CenterPanel>

        <RightPanel>
          <PropertiesPanel
            element={selectedElement}
            dispatch={dispatch}
            templateId={templateId}
            uploadLogo={uploadLogo}
            ensureTemplate={ensureTemplate}
          />
        </RightPanel>
      </EditorBody>
    </EditorRoot>
  );
}
